import { useAuthState } from '@auth/hooks';
import { routes } from '@communities/routes';
import { UserRoles } from '@core/domain';
import { routes as auditRoutes } from '@internal-audit/routes';
import { Icon, IconSize, ChevronLeft, Search, TestBeaker, ServerProcesses, Folder } from '@soteria/design-system-ve';
import { useNavigate } from 'react-router-dom';

import { config } from '../../../config';

import { CommunityImage } from './CommunityImage';
import * as styles from './Guide.css';
import type { GuideCommunity } from './useCommunityGroupsWithCommunities';

interface GuideCommunityPagesProps {
  community: GuideCommunity;
  communityGroupId: string | null;
  onBack: () => void;
  onClose: () => void;
}

/**
 * GuideCommunityPages component
 * Displays available pages for a selected community
 * Xbox-inspired compact navigation
 *
 * Authorization based on source code audit:
 * - Content Search & Classification Testing: All authenticated users (no restrictions)
 * - Audits: QA role only (CommunityAuditGuard enforces this)
 * - Community Details: All authenticated users (no restrictions)
 * - Community Group & Synesis: Admin role required
 */
export function GuideCommunityPages({ community, communityGroupId, onBack, onClose }: GuideCommunityPagesProps) {
  const navigate = useNavigate();
  const { hasRoleAccess } = useAuthState();

  // Authorization checks based on actual page requirements from source code audit
  const hasQAAccess = hasRoleAccess(UserRoles.QA, communityGroupId ?? undefined, community.id);
  const hasAdminAccess = hasRoleAccess(UserRoles.ADMIN, communityGroupId ?? undefined, community.id);

  const handlePageClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleSynesisClick = () => {
    const synesisUrl = config.externalServices.synesisUrl;

    if (synesisUrl) {
      // Open in same window to reuse session cookie
      window.location.href = synesisUrl;
    }
  };

  const handleCommunityHeaderClick = () => {
    navigate(routes.communities.detail(community.id));
    onClose();
  };

  const handleCommunityGroupClick = () => {
    if (communityGroupId) {
      navigate(routes.communityGroups.detail(communityGroupId));
      onClose();
    }
  };

  return (
    <div className={styles.pagesList}>
      {/* Back button */}
      <button aria-label="Back to communities" className={styles.pageButton} type="button" onClick={onBack}>
        <Icon className={styles.pageIcon} icon={ChevronLeft} iconSize={IconSize.Small} />
        <span>Back</span>
      </button>

      {/* Community header - clickable to navigate to community details */}
      <button aria-label={`Navigate to ${community.name} details`} className={styles.communityHeaderButton} type="button" onClick={handleCommunityHeaderClick}>
        <CommunityImage communityName={community.name} fetchPriority="high" loading="eager" logoUrl={community.logoUrl} />
        <div className={styles.communityHeaderContent}>
          <div className={styles.communityHeaderTitle}>{community.name}</div>
        </div>
      </button>

      {/* Content Search - Available to all authenticated users */}
      <button
        aria-label="Navigate to Content Search"
        className={styles.pageButton}
        type="button"
        onClick={() => handlePageClick(routes.communities.contentSearch.root(community.id))}
      >
        <Icon className={styles.pageIcon} icon={Search} iconSize={IconSize.Small} />
        <span>Content Search</span>
      </button>

      {/* Audits - Requires QA role (CommunityAuditGuard enforces QA-only access) */}
      {hasQAAccess && (
        <button
          aria-label="Navigate to Audits"
          className={styles.pageButton}
          type="button"
          onClick={() => handlePageClick(auditRoutes.internalAudit.community.audits(community.id))}
        >
          <Icon className={styles.pageIcon} icon={ServerProcesses} iconSize={IconSize.Small} />
          <span>Audits</span>
        </button>
      )}

      {/* Classification Testing - Available to all authenticated users */}
      <button
        aria-label="Navigate to Classification Testing"
        className={styles.pageButton}
        type="button"
        onClick={() => handlePageClick(routes.communities.classificationTesting.root(community.id))}
      >
        <Icon className={styles.pageIcon} icon={TestBeaker} iconSize={IconSize.Small} />
        <span>Classification Testing</span>
      </button>

      {/* Community Group - Requires admin access */}
      {communityGroupId && hasAdminAccess && (
        <button aria-label="Navigate to Community Group" className={styles.pageButton} type="button" onClick={handleCommunityGroupClick}>
          <Icon className={styles.pageIcon} icon={Folder} iconSize={IconSize.Small} />
          <span>Community Group</span>
        </button>
      )}

      {/* Synesis Data Management - Requires admin access */}
      {config.externalServices.synesisUrl && hasAdminAccess && (
        <button aria-label="Navigate to Synesis Data Management" className={styles.pageButton} type="button" onClick={handleSynesisClick}>
          <Icon className={styles.pageIcon} icon={ServerProcesses} iconSize={IconSize.Small} />
          <span>Synesis Data Management</span>
        </button>
      )}
    </div>
  );
}
